import { type ReactElement } from 'react';
import { Group, Text } from '@mantine/core';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import { formatTimestamp } from '@/shared/DateFormatting';
import ProviderIconComponent from '@/shared/ProviderIconComponent';
import InitialAvatar from '@/design/InitialAvatar';
import type { DirectoryUserRecord } from './DirectoryApiService';
import DirectoryStatusBadge from './DirectoryStatusBadge';

interface DirectoryTableProps {
    users: DirectoryUserRecord[];
    loading: boolean;
    connectionLabel: (connectionId: string) => string;
    onRowClick: (user: DirectoryUserRecord) => void;
}

export default function DirectoryTableComponent({ users, loading, connectionLabel, onRowClick }: DirectoryTableProps): ReactElement {
    const columns: MegaGridColumn<DirectoryUserRecord>[] = [
        {
            key: 'user',
            header: 'User',
            sortValue: (u) => u.displayName || u.email,
            render: (u) => (
                <Group gap="sm" wrap="nowrap">
                    <InitialAvatar source={u.displayName || u.email} size={28} />
                    <Text size="xs">{u.displayName || '—'}</Text>
                </Group>
            ),
        },
        { key: 'email', header: 'Email', sortValue: (u) => u.email, render: (u) => <Text size="xs">{u.email}</Text> },
        {
            key: 'department',
            header: 'Department',
            sortValue: (u) => u.department,
            groupValue: (u) => u.department ?? '—',
            filter: { type: 'enum', accessor: (u) => u.department ?? '' },
            render: (u) => <Text size="xs">{u.department ?? '—'}</Text>,
        },
        { key: 'title', header: 'Title', sortValue: (u) => u.title, render: (u) => <Text size="xs">{u.title ?? '—'}</Text> },
        {
            key: 'status',
            header: 'Status',
            sortValue: (u) => u.status,
            filter: { type: 'enum', accessor: (u) => u.status },
            render: (u) => <DirectoryStatusBadge status={u.status} />,
        },
        {
            key: 'source',
            header: 'Source',
            sortValue: (u) => connectionLabel(u.connectionId),
            render: (u) => (
                <Group gap="xs" wrap="nowrap">
                    <ProviderIconComponent providerTypeId={u.providerTypeId} size={14} />
                    <Text size="xs">{connectionLabel(u.connectionId)}</Text>
                </Group>
            ),
        },
        {
            key: 'syncedAt',
            header: 'Synced',
            align: 'right',
            sortValue: (u) => new Date(u.syncedAt),
            render: (u) => (
                <Text size="xs" c="dimmed">
                    {formatTimestamp(u.syncedAt)}
                </Text>
            ),
        },
    ];

    return (
        <MegaGrid
            persistenceKey="directory-users"
            columns={columns}
            rows={users}
            getRowKey={(u) => `${u.connectionId}-${u.externalId}`}
            onRowClick={onRowClick}
            loading={loading}
            searchAccessor={(u) => `${u.displayName ?? ''} ${u.email} ${u.department ?? ''} ${u.title ?? ''} ${connectionLabel(u.connectionId)}`}
            searchPlaceholder="Search users"
            clientPageSize={25}
            maxHeight={480}
            emptyMessage="No directory users yet — run a sync to populate."
            noMatchMessage="No users match the current filters."
        />
    );
}
